import { CashIcon, OpenInNewTabIcon, SuccessIcon } from "@src/assets";
import { ReactNode, useEffect, useMemo } from "react";
import { shortAddress } from "@src/utils";
import { TokenWithChain } from "@src/models";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
  Cashback,
  IconWithTooltip,
  TokenLogo,
} from "@src/components";
import { CASH_TOKEN_ADDRESS_BY_CHAIN } from "@src/config";
import { useNavigate } from "react-router-dom";
import { RoutePath, SwapMode } from "@src/interfaces";
import { useSwapContext } from "@src/contexts";
import { PointsReward } from "@src/components/PointsReward";

interface Props {
  title: string;
  amount: string;
  txHash?: string;
  explorerUrl?: string;
  token?: TokenWithChain;
  displayToken?: TokenWithChain;
  button?: ReactNode;
  footer?: ReactNode;
  forceUSDC?: boolean;
  pointsAmount?: string;
  cashbackAmount?: string;
  fromBuyOrSell?: boolean;
}

export const SuccessView = ({
  title,
  amount,
  txHash,
  explorerUrl,
  token,
  displayToken,
  button,
  footer,
  forceUSDC,
  pointsAmount,
  cashbackAmount,
  fromBuyOrSell = false,
}: Props) => {
  const navigate = useNavigate();
  const { swapMode, resetSwapFormData, fetchHistory, ctas, chain } =
    useSwapContext();
  const isCash = useMemo(() => {
    if (forceUSDC) return false;
    if (
      !token ||
      token.address.toLowerCase() ===
        CASH_TOKEN_ADDRESS_BY_CHAIN[token.chainId].toLowerCase()
    )
      return true;
    return false;
  }, [token, forceUSDC]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const displayCustomCTAButton = useMemo(
    () =>
      fromBuyOrSell &&
      ctas.successViewButton?.url &&
      ctas.successViewButton?.label,
    [ctas, fromBuyOrSell],
  );

  return (
    <div className="flex flex-col gap-5 h-full w-full items-center justify-center">
      <SuccessIcon className="w-[50px] h-[50px] !fill-theme_color" />
      <div className="text-2xl font-bold text-text_primary">{title}</div>
      <div className="bg-card_bg rounded-[12px] h-[60px] flex w-full items-center justify-center">
        <div className="flex gap-2 items-end">
          <div className="font-bold text-text_primary text-[30px]">
            {`${isCash ? "$" : ""}${amount}`}
          </div>
          {isCash ? (
            <>
              <CashIcon className="w-6 h-6" />
              <div className="flex items-center gap-1">
                <div className="font-medium text-text_primary text-xs">
                  Cash
                </div>
                <IconWithTooltip
                  id="cash-tooltip"
                  tooltipText="Cash balances are held in USDC, a fully collateralize stablecoin"
                />
              </div>
            </>
          ) : (
            <div className="flex gap-1 items-end">
              <TokenLogo
                token={displayToken || token}
                tokenLogoClassName="w-6 min-w-6 h-6"
                unknownTokenLogoClassName="w-6 min-w-6 h-6 text-[14px]"
                chain={chain}
              />
              <div className="text-xs font-medium text-text_primary leading-3">
                {displayToken?.symbol || token?.symbol}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2 w-full">
        {pointsAmount && <PointsReward amount={pointsAmount} />}
        {cashbackAmount && <Cashback amount={cashbackAmount} />}
      </div>
      <div className="flex flex-1" />

      <div className="flex flex-col gap-2 w-full">
        {footer}
        {displayCustomCTAButton && (
          <Button
            variant={ButtonVariants.Primary}
            onClick={() => {
              window.open(ctas.successViewButton?.url, "_blank");
            }}
            size={ButtonSizes.Large}
          >
            {ctas.successViewButton?.label}
          </Button>
        )}
        {button ? (
          button
        ) : (
          <Button
            variant={
              displayCustomCTAButton
                ? ButtonVariants.Outlined
                : ButtonVariants.Primary
            }
            onClick={() => {
              resetSwapFormData();
              navigate(
                swapMode === SwapMode.Buy ? RoutePath.Buy : RoutePath.Sell,
              );
            }}
            size={ButtonSizes.Large}
          >
            Proceed
          </Button>
        )}
      </div>
      {txHash && explorerUrl && (
        <div
          onClick={() => window.open(`${explorerUrl}/${txHash}`)}
          className="flex items-center gap-1 cursor-pointer p-2"
        >
          <div className="text-xs text-text_primary opacity-60">TxHash:</div>
          <div className="text-sm font-bold text-text_primary">
            {shortAddress(txHash)}
          </div>
          <OpenInNewTabIcon />
        </div>
      )}
    </div>
  );
};
